import type { Participant, UseCaseData, UseCaseFormData } from "../types/use-case.types"

import { api } from "@/lib/api-client"

export async function listUseCases(): Promise<UseCaseData[]> {
  const response = await api.get("/usecases")
  return response.data || response
}

export async function getUseCase(id: string): Promise<UseCaseData> {
  const response = await api.get(`/usecases/${id}`)
  return response.data || response
}

export async function createUseCase(data: UseCaseFormData): Promise<UseCaseData> {
  const response = await api.post("/usecases", data)
  return response.data || response
}

export async function updateUseCase(
  id: string,
  data: Partial<UseCaseFormData>
): Promise<UseCaseData> {
  const response = await api.put(`/usecases/${id}`, data)
  return response.data || response
}

export async function deleteUseCase(id: string): Promise<void> {
  await api.delete(`/usecases/${id}`)
}

export async function setUnrestricted(
  id: string,
  isUnrestricted: boolean
): Promise<{ success: boolean; isUnrestricted: boolean }> {
  const response = await api.post(`/usecases/${id}/unrestricted`, { isUnrestricted })
  return response.data || response
}

// Participants
export async function listParticipants(useCaseId: string): Promise<Participant[]> {
  const response = await api.get(`/usecases/${useCaseId}/participants`)
  return response.data || response
}

export async function addParticipant(
  useCaseId: string,
  participant: { oid: string; displayName: string; type: "user" | "group"; role: string }
): Promise<Participant> {
  const response = await api.post(`/usecases/${useCaseId}/participants`, participant)
  return response.data || response
}

export async function updateParticipantRole(
  useCaseId: string,
  participantOid: string,
  role: string
): Promise<void> {
  await api.put(`/usecases/${useCaseId}/participants/${participantOid}`, { role })
}

export async function removeParticipant(useCaseId: string, participantOid: string): Promise<void> {
  await api.delete(`/usecases/${useCaseId}/participants/${participantOid}`)
}

// Import / Export
export async function importUseCases(files: File[]): Promise<UseCaseData[]> {
  const formData = new FormData()
  files.forEach((file) => formData.append("files", file))
  const response = await api.post("/usecases/import", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  })
  return response.data || response
}

export async function exportUseCase(id: string): Promise<Blob> {
  const response = await api.get(`/usecases/${id}/export`, { responseType: "blob" })
  return response.data || response
}

// Voting
export async function toggleVote(
  useCaseId: string
): Promise<{ voted: boolean; voteCount: number }> {
  const response = await api.post(`/usecases/${useCaseId}/vote`)
  return response.data || response
}

// Admin
export async function listAllUseCasesAdmin(): Promise<UseCaseData[]> {
  const response = await api.get("/usecases/admin/all")
  return response.data || response
}

// AI Features
export async function aiDraftAssist(useCaseData: Partial<UseCaseFormData>, targetField: string) {
  const response = await api.post("/usecases/ai/draft-assist", { useCaseData, targetField })
  return response.data || response
}

export async function aiQaReview(useCaseData: Partial<UseCaseFormData>, useCaseId?: string) {
  const response = await api.post("/usecases/ai/qa-review", { useCaseData, useCaseId })
  return response.data || response
}

export async function aiSalesPitch(useCaseData: Partial<UseCaseFormData>, useCaseId?: string) {
  const response = await api.post("/usecases/ai/sales-pitch", { useCaseData, useCaseId })
  return response.data || response
}

export async function aiClaudePrompt(useCaseData: Partial<UseCaseFormData>) {
  const response = await api.post("/usecases/ai/claude-prompt", { useCaseData })
  return response.data || response
}

export async function aiGenerateFull(answers: {
  problem: string
  users: string
  sites: string[]
  solution: string
  dataSources: string
}): Promise<UseCaseFormData> {
  const response = await api.post("/usecases/ai/generate-full", answers)
  return response.data || response
}
